import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Home from './pages/Home';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import ADR from './pages/ADR';
import NotificationTrigger from './pages/NotificationTrigger';
import { NotificationProvider } from './contexts/NotificationContext';
import { MedicationProvider } from './contexts/MedicationContext';
import DashboardLogin from './pages/dashboard/Login';
import DashboardWithMap from './pages/dashboard/DashboardWithMap';
import PrivateRoute from './components/PrivateRoute';
import LandingPage from './pages/LandingPage';
import SMSPreviewPage from './pages/SMSPreview';
import Signup from './pages/Signup';
import SignupSuccess from './pages/SignupSuccess';
import './App.css';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#0a1c30', // Dark navy blue
      light: '#2c3e50',
      dark: '#050e17',
      contrastText: '#fff'
    },
    secondary: {
      main: '#64c0fa', // Light blue from logo
      light: '#97d4fb',
      dark: '#3193d4',
      contrastText: '#0a1c30'
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff'
    },
    text: {
      primary: '#0a1c30',
      secondary: '#475569'
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#fff'
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#fff'
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#fff'
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#fff'
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        color: 'primary'
      },
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          borderRadius: 8
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        containedPrimary: {
          backgroundColor: '#64c0fa',
          color: '#0a1c30',
          '&:hover': {
            backgroundColor: '#3193d4',
          },
        },
        outlinedPrimary: {
          borderColor: '#64c0fa',
          color: '#64c0fa',
          '&:hover': {
            borderColor: '#3193d4',
            color: '#3193d4',
          },
        },
        containedSecondary: {
          backgroundColor: '#64c0fa',
          '&:hover': {
            backgroundColor: '#3193d4',
          },
        },
        outlinedSecondary: {
          borderColor: '#64c0fa',
          color: '#64c0fa',
          '&:hover': {
            borderColor: '#3193d4',
            color: '#3193d4',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-selected': {
            color: '#64c0fa'
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#0a1c30',
          '&:hover': {
            backgroundColor: 'rgba(100, 192, 250, 0.04)'
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#64c0fa'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  }
});

function AppContent() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard/login" element={<DashboardLogin />} />
      
      {/* Patient/Caregiver/Family Member routes with toolbar navigation */}
      <Route path="/patient" element={
        <PrivateRoute>
          <Home />
        </PrivateRoute>
      } />
      <Route path="/profile" element={
        <PrivateRoute>
          <Profile />
        </PrivateRoute>
      } />
      <Route path="/notifications" element={
        <PrivateRoute>
          <Notifications />
        </PrivateRoute>
      } />
      <Route path="/adr" element={
        <PrivateRoute>
          <ADR />
        </PrivateRoute>
      } />
      <Route path="/trigger-notifications" element={
        <PrivateRoute>
          <NotificationTrigger />
        </PrivateRoute>
      } />
      
      {/* Healthcare provider dashboard routes */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <DashboardWithMap />
        </PrivateRoute>
      } />
      <Route path="/dashboard/map" element={
        <PrivateRoute>
          <DashboardWithMap />
        </PrivateRoute>
      } />
      
      {/* SMS Preview route */}
      <Route path="/SMSPreview" element={<SMSPreviewPage />} />
      
      {/* Signup routes */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup-success" element={<SignupSuccess />} />
      
      {/* Redirect all other routes to landing page */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <MedicationProvider>
          <AppContent />
        </MedicationProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
